import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { useResolvedPhotoUrl } from "@/components/ui-extras";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/scan-history")({
  head: () => ({
    meta: [
      { title: "Scan History — Cailève" },
      { name: "description", content: "Every piece you have photographed with Cailève Eye." },
    ],
  }),
  component: () => (
    <AppShell>
      <ScanHistory />
    </AppShell>
  ),
});

const SERIF = "'Cormorant Garamond', Georgia, serif";

type ScanRow = {
  id: string;
  scanned_at: string;
  verdict_type: string | null;
  item_description: string | null;
  item_category: string | null;
  verdict_copy: string | null;
  scanned_photo_path: string | null;
  item_saved: boolean;
};

const VERDICT_LABELS: Record<string, string> = {
  fits: "This is you",
  gap: "Fills a gap",
  duplicate: "You have this",
  wait: "Not yet",
  stretch: "A stretch",
  thin: "Still growing",
  out_of_scope: "Out of scope",
};

const VERDICT_COLORS: Record<string, string> = {
  fits: "var(--caileve-gold)",
  gap: "var(--caileve-lilac)",
  duplicate: "var(--caileve-muted)",
  wait: "var(--caileve-muted)",
  stretch: "var(--caileve-lilac)",
  thin: "var(--caileve-muted)",
  out_of_scope: "var(--caileve-muted)",
};

function groupIntoTrips(scans: ScanRow[]): { date: string; scans: ScanRow[] }[] {
  if (scans.length === 0) return [];
  const trips: { date: string; scans: ScanRow[] }[] = [];
  let currentTrip: ScanRow[] = [scans[0]];
  let tripAnchor = new Date(scans[0].scanned_at).getTime();

  for (let i = 1; i < scans.length; i++) {
    const t = new Date(scans[i].scanned_at).getTime();
    if (tripAnchor - t <= 8 * 60 * 60 * 1000) {
      currentTrip.push(scans[i]);
    } else {
      trips.push({ date: currentTrip[0].scanned_at, scans: currentTrip });
      currentTrip = [scans[i]];
      tripAnchor = t;
    }
  }
  trips.push({ date: currentTrip[0].scanned_at, scans: currentTrip });
  return trips;
}

function formatTripDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return d.toLocaleDateString("en-US", { weekday: "long" });
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function ScanThumb({
  photoPath,
  description,
  verdictType,
  verdictColor,
}: {
  photoPath: string | null;
  description: string | null;
  verdictType: string | null;
  verdictColor: string;
}) {
  const { url } = useResolvedPhotoUrl(photoPath);
  return (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 14,
        overflow: "hidden",
        backgroundColor: "var(--caileve-soft)",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {url ? (
        <img
          src={url}
          alt={description ?? ""}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            padding: 6,
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: verdictColor,
              marginBottom: 4,
            }}
          />
          {verdictType && (
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: verdictColor,
                textAlign: "center",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              {VERDICT_LABELS[verdictType] ?? verdictType}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ScanCard({
  scan,
  userId,
  onSaved,
  onDelete,
  editMode,
  selected,
  onToggleSelect,
}: {
  scan: ScanRow;
  userId: string;
  onSaved: (id: string) => void;
  onDelete: (id: string) => void;
  editMode: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(scan.item_saved);

  const verdictLabel = scan.verdict_type ? (VERDICT_LABELS[scan.verdict_type] ?? scan.verdict_type) : null;
  const verdictColor = scan.verdict_type ? (VERDICT_COLORS[scan.verdict_type] ?? "var(--caileve-muted)") : "var(--caileve-muted)";

  const handleAddToWishlist = async () => {
    if (saving || saved) return;
    setSaving(true);
    try {
      const categoryFallback = scan.item_category === "clothing" ? "Scanned clothing" : scan.item_category === "accessory" ? "Scanned accessory" : "Scanned jewelry";
      const name = scan.item_description?.trim() || categoryFallback;
      const table = scan.item_category === "clothing" ? "clothing_items" : "jewelry_items";
      const extra =
        scan.item_category === "accessory"
          ? { is_accessory: true }
          : scan.item_category === "clothing"
          ? {}
          : { is_accessory: false };
      const { error } = await supabase.from(table).insert({
        user_id: userId,
        name,
        is_wishlist: true,
        photo_url: scan.scanned_photo_path,
        eye_verdict: scan.verdict_copy,
        ...extra,
      } as never);
      if (error) throw new Error(error.message);
      await supabase.from("ecrin_eye_scans").update({ item_saved: true } as never).eq("id", scan.id);
      setSaved(true);
      onSaved(scan.id);
      track("scan_history_add_to_wishlist", { verdict_type: scan.verdict_type });
      toast.success("Added to Your Edit.");
      if (scan.item_category === "clothing") {
        qc.invalidateQueries({ queryKey: ["clothing_wishlist", userId] });
      } else {
        qc.invalidateQueries({ queryKey: ["jewelry_wishlist", userId] });
      }
    } catch {
      toast.error("Could not save piece.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from("ecrin_eye_scans")
        .delete()
        .eq("id", scan.id);
      if (error) throw new Error(error.message);
      onDelete(scan.id);
      track("scan_history_deleted");
    } catch {
      toast.error("Could not remove scan.");
    }
  };

  return (
    <div
      style={{
        borderBottom: "1px solid var(--caileve-soft)",
        paddingTop: 16,
        paddingBottom: 16,
      }}
    >
      <button
        type="button"
        onClick={() => editMode ? onToggleSelect(scan.id) : setExpanded((e) => !e)}
        style={{
          display: "flex",
          gap: 14,
          alignItems: "flex-start",
          width: "100%",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          padding: 0,
        }}
      >
        <div style={{ position: "relative", flexShrink: 0 }}>
          <ScanThumb
            photoPath={scan.scanned_photo_path}
            description={scan.item_description}
            verdictType={scan.verdict_type}
            verdictColor={verdictColor}
          />
          {editMode && (
            <div
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: selected ? "var(--caileve-lilac)" : "rgba(255,255,255,0.85)",
                border: selected ? "none" : "1.5px solid var(--caileve-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.15s",
              }}
            >
              {selected && (
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="var(--caileve-bg)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: 17,
              color: "var(--caileve-ink)",
              lineHeight: 1.3,
            }}
          >
            {scan.item_description ?? "Unidentified piece"}
          </div>
          {verdictLabel && (
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: verdictColor,
                marginTop: 6,
              }}
            >
              {verdictLabel}
            </div>
          )}
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 11,
              color: "var(--caileve-muted)",
              marginTop: 4,
            }}
          >
            {new Date(scan.scanned_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </div>
        </div>
      </button>

      {!editMode && expanded && (
        <div style={{ marginTop: 12, paddingLeft: 78 }}>
          {scan.verdict_copy ? (
            <p
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: 15,
                color: "var(--caileve-ink)",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {scan.verdict_copy}
            </p>
          ) : (
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                color: "var(--caileve-muted)",
                margin: 0,
              }}
            >
              No reasoning recorded for this scan.
            </p>
          )}
          {!saved ? (
            <button
              type="button"
              onClick={handleAddToWishlist}
              disabled={saving}
              style={{
                marginTop: 12,
                background: "none",
                border: "1px solid var(--caileve-lilac)",
                color: "var(--caileve-lilac)",
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                padding: "8px 16px",
                cursor: saving ? "default" : "pointer",
                opacity: saving ? 0.5 : 1,
              }}
            >
              {saving ? "Saving…" : "Add to Your Edit"}
            </button>
          ) : (
            <div
              style={{
                marginTop: 12,
                fontFamily: "Inter, sans-serif",
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "var(--caileve-muted)",
              }}
            >
              In Your Edit
            </div>
          )}
          <button
            type="button"
            onClick={handleDelete}
            style={{
              marginTop: 8,
              display: "block",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "var(--caileve-muted)",
              padding: 0,
            }}
          >
            Remove from history
          </button>
        </div>
      )}
    </div>
  );
}

function ScanHistory() {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0 || deleting) return;
    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const { error } = await supabase
        .from("ecrin_eye_scans")
        .delete()
        .in("id", ids);
      if (error) throw new Error(error.message);
      track("scan_history_batch_deleted", { count: ids.length });
      setSelectedIds(new Set());
      setEditMode(false);
      qc.invalidateQueries({ queryKey: ["scan_history", uid] });
      toast.success(`${ids.length} ${ids.length === 1 ? "scan" : "scans"} removed.`);
    } catch {
      toast.error("Could not remove scans.");
    } finally {
      setDeleting(false);
    }
  };

  const { data: scans, isLoading } = useQuery({
    queryKey: ["scan_history", uid],
    enabled: !!uid,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { data, error } = await supabase
        .from("ecrin_eye_scans")
        .select(
          "id, scanned_at, verdict_type, item_description, item_category, verdict_copy, scanned_photo_path, item_saved",
        )
        .eq("user_id", uid!)
        .gte("scanned_at", thirtyDaysAgo.toISOString())
        .order("scanned_at", { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []) as ScanRow[];
    },
  });

  const trips = scans ? groupIntoTrips(scans) : [];

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => {
              if (editMode) { setEditMode(false); setSelectedIds(new Set()); }
              else navigate({ to: "/caileve-eye", search: { token: undefined, mode: undefined } });
            }}
            className="inline-flex items-center text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <ChevronLeft size={14} /> {editMode ? "Cancel" : "Eye"}
          </button>
          {!isLoading && (scans?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => { setEditMode((v) => !v); setSelectedIds(new Set()); }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: editMode ? "var(--caileve-lilac)" : "var(--caileve-muted)",
                padding: 0,
              }}
            >
              {editMode ? "Done" : "Edit"}
            </button>
          )}
        </div>
        <h1
          style={{
            fontFamily: SERIF,
            fontSize: 32,
            color: "var(--caileve-ink)",
            marginTop: 12,
            marginBottom: 4,
          }}
        >
          Scan History
        </h1>
        <p
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: 15,
            color: "var(--caileve-muted)",
            margin: 0,
          }}
        >
          Every piece you have photographed with Cailève Eye.
        </p>
      </div>

      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[1, 2, 3].map((n) => (
            <div key={n} style={{ display: "flex", gap: 14 }}>
              <div style={{ width: 64, height: 64, borderRadius: 14, backgroundColor: "var(--caileve-soft)" }} />
              <div style={{ flex: 1 }}>
                <div style={{ height: 16, width: "60%", backgroundColor: "var(--caileve-soft)", marginBottom: 8 }} />
                <div style={{ height: 10, width: "40%", backgroundColor: "var(--caileve-soft)" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && trips.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 16px" }}>
          <p style={{ fontFamily: SERIF, fontSize: 20, color: "var(--caileve-ink)", marginBottom: 8 }}>
            No scans yet.
          </p>
          <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, color: "var(--caileve-muted)", margin: 0 }}>
            The next time you see a piece in a store, photograph it with Cailève Eye.
          </p>
        </div>
      )}

      {!isLoading &&
        trips.map((trip, i) => (
          <div key={i} style={{ marginBottom: 32 }}>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "var(--caileve-muted)",
                paddingBottom: 8,
                borderBottom: "1px solid var(--caileve-gold)",
              }}
            >
              {formatTripDate(trip.date)} · {trip.scans.length} {trip.scans.length === 1 ? "piece" : "pieces"}
            </div>
            {trip.scans.map((scan) => (
              <ScanCard
                key={scan.id}
                scan={scan}
                userId={uid!}
                onSaved={() => {}}
                onDelete={() => qc.invalidateQueries({ queryKey: ["scan_history", uid] })}
                editMode={editMode}
                selected={selectedIds.has(scan.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
        ))}
        {editMode && selectedIds.size > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: "calc(env(safe-area-inset-bottom) + 80px)",
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              padding: "0 24px",
              zIndex: 50,
            }}
          >
            <button
              type="button"
              onClick={handleBatchDelete}
              disabled={deleting}
              style={{
                backgroundColor: "var(--caileve-lilac)",
                color: "var(--caileve-bg)",
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                border: "none",
                borderRadius: 4,
                padding: "14px 32px",
                cursor: deleting ? "default" : "pointer",
                opacity: deleting ? 0.6 : 1,
                width: "100%",
                maxWidth: 400,
              }}
            >
              {deleting ? "Removing…" : `Remove ${selectedIds.size} ${selectedIds.size === 1 ? "scan" : "scans"}`}
            </button>
          </div>
        )}
    </div>
  );
}